/*
 * Username blocklists.
 *
 * Data only: the matching logic lives in `username-policy.ts`. Every term here
 * is matched after normalisation (case, diacritics, homoglyphs, leetspeak and
 * repeated letters are all folded away), so write terms in plain lowercase
 * letters and do not add spelling variants: `admin` already covers `4dm1n`,
 * `ADMIN`, `aaadmin`, `admın` and `_admin_`.
 *
 * Tiers:
 *   RESERVED_ROUTES   paths and sentinels that must not be a profile handle
 *   AUTHORITY_TERMS   staff/role impersonation
 *   TEST_TERMS        throwaway and placeholder accounts
 *   COMPANY_NAMES     major brands (trademark/impersonation)
 *   SLUR_STEMS        hate slurs                     (see username-slurs note)
 *   HATE_TERMS        extremism, terror, child abuse
 *   PROFANITY_STEMS   crude language
 *   INNOCENT_WORDS    allowlist that suppresses substring false positives
 */

/** This product's own name; expanded into `<brand>`, `<brand>admin`, `<brand>support`, ... */
export const BRAND = "atrium";

/** Routes, API prefixes and sentinel values. A handle equal to one of these breaks or spoofs URLs. */
export const RESERVED_ROUTES = [
  "about", "account", "accounts", "admin", "api", "app", "apps", "assets", "auth", "avatar", "avatars",
  "badge", "badges", "billing", "blog", "bookmark", "bookmarks", "callback", "cdn", "channel", "channels",
  "chat", "communities", "community", "compose", "contact", "cookies", "copyright", "dashboard", "deleted",
  "developer", "developers", "discover", "dm", "dms", "docs", "download", "downloads", "edit", "editor",
  "email", "embed", "explore", "export", "favicon", "feed", "feedback", "files", "follow", "followers",
  "following", "forgot", "friends", "graphql", "group", "groups", "health", "help", "home", "image",
  "images", "img", "inbox", "index", "invite", "invites", "join", "legal", "license", "like", "likes",
  "login", "logout", "mail", "media", "message", "messages", "mobile", "new", "news", "notification",
  "notifications", "oauth", "onboarding", "p", "page", "pages", "password", "payment", "payments", "post",
  "posts", "preferences", "premium", "pricing", "privacy", "profile", "profiles", "public", "register",
  "report", "reports", "reset", "robots", "rss", "search", "security", "session", "sessions",
  "settings", "signin", "signout", "signup", "sitemap", "static", "status", "store", "subscribe", "tag",
  "tags", "terms", "thread", "threads", "trending", "u", "upload", "uploads", "user", "users", "verify",
  "webhook", "webhooks", "ws",
  // Sentinels: these read as "no user" in logs, URLs and templates.
  "all", "anon", "anonymous", "any", "everyone", "false", "guest", "here", "me", "nan", "nobody", "none",
  "null", "self", "somebody", "someone", "true", "undefined", "unknown", "void", "you",
];

/**
 * Strong staff claims. Blocked as a whole handle *and* as a token anywhere in
 * one, because `atrium_support_team` is as misleading as `support`.
 */
export const AUTHORITY_TERMS = [
  "abuse", "admin", "administrator", "administration", "automod", "customercare", "customerservice",
  "customersupport", "devteam", "enforcement", "helpdesk", "hostmaster", "moderator", "moderation",
  "modteam", "mod", "mods", "noreply", "official", "postmaster", "root", "safetyteam", "securityteam",
  "staff", "support", "sysadmin", "sysop", "systemadmin", "systembot", "trustandsafety", "verification",
  "verified", "webmaster",
];

/**
 * Generic role words. Blocked only as the whole handle (after filler is peeled),
 * since `project_manager` or `team_rocket_fan` are honest handles.
 */
export const ROLE_HANDLE_TERMS = [
  "bot", "ceo", "cfo", "chatbot", "cofounder", "compliance", "cto", "dev", "developer", "employee",
  "founder", "legalteam", "manager", "operator", "owner", "safety", "security", "system", "team",
];

/** Throwaway, placeholder and QA accounts. */
export const TEST_TERMS = [
  "asdf", "asdfasdf", "dummy", "dummyaccount", "dummyuser", "example", "exampleuser", "fakeaccount",
  "fakeuser", "foo", "foobar", "donotuse", "ignoreme", "lorem", "loremipsum", "notreal",
  "placeholder", "qa", "qatest", "qatester", "sample", "sampleuser", "sandbox", "staging", "temp",
  "tempaccount", "tempuser", "test", "testaccount", "testacct", "testbot", "tester", "testing",
  "testuser", "throwaway", "tmp", "tryme", "xxx",
];

/*
 * Major company, product and platform names. Impersonating these invites
 * trademark complaints and is a common phishing setup ("paypal_support").
 *
 * Deliberately excluded: brands that are also ordinary English words or common
 * names (Target, Subway, Coke, Puma, Signal, Medium, Shell, Orange, Visa as a
 * word, Apple on its own is kept because impersonation risk outweighs it).
 * Add a brand here only if the word is unlikely to be someone's honest handle.
 */
export const COMPANY_NAMES = [
  // Big tech and platforms
  "adobe", "airbnb", "alibaba", "aliexpress", "alphabet", "amazon", "android", "anthropic", "apple", "aws", "azure",
  "bing", "canva", "chatgpt", "chrome", "claude", "cloudflare", "copilot", "deepmind",
  "deepseek", "discord", "dropbox", "duckduckgo", "ebay", "facebook", "figma", "firefox", "github",
  "gitlab", "gmail", "google", "googleplay", "huawei", "ibm", "icloud", "instagram", "intel", "linkedin",
  "messenger", "meta", "microsoft", "midjourney", "mistral", "mozilla", "netflix", "netlify",
  "nvidia", "openai", "oracle", "outlook", "patreon", "perplexity", "pinterest", "quora",
  "reddit", "safari", "salesforce", "samsung", "shopify", "slack", "snapchat", "softbank", "spotify",
  "substack", "telegram", "tencent", "tiktok", "tinder", "tumblr", "twitch", "twitter", "uber", "vercel",
  "vimeo", "vkontakte", "whatsapp", "wikipedia", "windows", "xbox", "xiaomi", "yahoo", "yandex", "youtube",
  "zoom",
  // Gaming
  "activision", "battlenet", "bethesda", "blizzard", "epicgames", "fortnite", "minecraft", "mojang",
  "nintendo", "playstation", "riotgames", "roblox", "rockstargames", "steam", "steampowered", "ubisoft",
  // Money
  "amex", "binance", "bitcoin", "cashapp", "citibank", "coinbase", "revolut", "klarna", "mastercard",
  "monzo", "paypal", "stripe", "venmo", "barclays", "hsbc", "santander", "wellsfargo", "robinhood",
  // Consumer and industrial
  "adidas", "audi", "bmw", "boeing", "cocacola", "disney", "ferrari", "gucci", "hyundai", "ikea",
  "kfc", "lamborghini", "louisvuitton", "mcdonalds", "mercedes", "nestle", "nike", "pepsi", "porsche",
  "prada", "rolex", "samsonite", "sephora", "spacex", "starbucks", "tesla", "toyota", "unilever",
  "volkswagen", "walmart", "zara",
  // Delivery, telco, travel
  "dhl", "doordash", "fedex", "grubhub", "instacart", "lyft", "ryanair", "verizon", "vodafone",
];

/*
 * Hate slurs, matched as substrings (long stems) or whole handle tokens (short
 * stems). This list is intentionally short and high-confidence: it covers the
 * unambiguous cases only. For production coverage across languages, layer a
 * maintained dataset on top (for example the `obscenity` npm package or the
 * LDNOOBW word lists) and feed it in via `extraSlurs` - the matcher treats
 * extra terms exactly like these.
 *
 * Ambiguous terms are left out on purpose: short words that double as surnames,
 * place names or non-English words produce more false accusations than catches.
 */
export const SLUR_STEMS = [
  "nigger", "nigga", "niglet", "chink", "gook", "kike", "spic", "wetback", "beaner", "coon", "jigaboo",
  "darkie", "darky", "raghead", "towelhead", "cameljockey", "currymuncher", "wop", "dago", "polack",
  "zipperhead", "halfbreed", "redskin", "squaw", "injun", "pickaninny", "junglebunny", "porchmonkey",
  "shitskin", "muzzie", "christkiller", "faggot", "fagot", "fag", "dyke", "tranny", "trannie", "shemale",
  "ladyboy", "retard", "retarded", "mongoloid", "spastic", "paki", "abo", "jap",
];

/** Extremism, terror and child-abuse references. Never allowed, in any position. */
export const HATE_TERMS = [
  "nazi", "neonazi", "hitler", "adolfhitler", "heilhitler", "siegheil", "whitepower", "whitepride",
  "aryannation", "bloodandsoil", "holohoax", "holocaustdenial", "kkk", "kuklux", "kluxklan", "lynching",
  "gaschamber", "1488", "fourteenwords", "isis", "alqaeda", "taliban", "bokoharam", "hamas", "massshooter",
  "schoolshooter", "pedo", "pedophile", "paedophile", "childporn", "childp", "loli", "lolicon",
  "rape", "rapist", "molester", "groomer",
];

/** Crude language. Blocked for handles because handles are public and unavoidable. */
export const PROFANITY_STEMS = [
  "fuck", "phuck", "phuk", "fuk", "fvck", "shyt", "motherfucker", "shit", "bullshit", "cunt", "bitch", "whore", "slut", "asshole", "arsehole",
  "bastard", "wanker", "wank", "dildo", "pussy", "penis", "vagina", "boner", "cumshot", "blowjob",
  "handjob", "titties", "porn", "pornhub", "hentai", "nutsack", "ballsack", "scrotum", "jizz", "prick",
  "twat", "knobhead", "dickhead", "cock", "dick",
];

/**
 * Words people bolt onto a protected name to build a convincing fake:
 * `free_netflix`, `paypal_refunds`, `atriumhelpdesk`. A handle made of nothing
 * but qualifiers plus a protected name is treated as that name.
 */
export const QUALIFIER_WORDS = [
  "the", "real", "official", "offical", "my", "its", "join", "free", "get", "buy", "shop", "store",
  "deal", "deals", "code", "codes", "promo", "coupon", "giveaway", "gift", "gifts", "prize", "prizes",
  "claim", "reward", "rewards", "win", "winner", "login", "signin", "verify", "verified", "verification",
  "account", "accounts", "billing", "payment", "payments", "refund", "refunds", "invoice", "help",
  "helpdesk", "support", "service", "services", "care", "center", "centre", "team", "hq", "staff",
  "admin", "mod", "bot", "news", "update", "updates", "app", "inc", "global", "online", "plus", "pro",
  "us", "uk", "eu",
];

/*
 * Innocent words that contain a blocked stem. Matched first and masked out, so
 * the stem inside them is never seen - the Scunthorpe guard. Add to this list
 * whenever a real signup is wrongly rejected.
 */
export const INNOCENT_WORDS = [
  // places and nationalities
  "pakistan", "pakistani", "japan", "japanese", "japon", "nigeria", "nigerian", "niger", "scunthorpe",
  "penistone", "cumbria", "cumberland", "lincoln", "sussex", "essex", "middlesex", "devon", "canada",
  "titicaca", "phuket", "cockermouth",
  // names and surnames
  "cockburn", "cocker", "peacock", "hitchcock", "dickson", "dickens", "dickinson", "benedict", "fagan",
  "fagin", "cooney", "mccoon", "vandyke", "dykstra", "titus", "titan", "spicer", "coonan", "babcock",
  // ordinary words
  "analysis", "analyst", "analytics", "canal", "assassin", "assess", "classic", "class", "grass", "bass",
  "massive", "passion", "cocktail", "cockney", "cockpit", "cockatoo", "raccoon", "cocoon", "tycoon",
  "cumulative", "accumulate", "circumstance", "document", "documentary", "spice", "spicy", "despicable",
  "auspicious", "suspicion", "hospice", "episcopal", "shiitake", "shitake", "titanic", "title", "titled",
  "constitution", "institution", "competition", "dictionary", "predict", "verdict", "faculty", "scrape",
  "scraper", "grape", "grapes", "drapery", "therapy", "therapist", "paragraph", "crapshoot", "abode",
  "about", "above", "abort", "abolish", "label", "labor", "taboo", "wopat", "swoop", "scoop",
  "cockroach", "shuttlecock", "weathercock", "stopcock", "dickie", "dicky", "spicule", "cooperate",
  "coordinate", "shitzu", "shihtzu", "spiced", "suspicious", "suspicions", "woodcock", "cockle",
  "cockatiel", "cockatrice", "snigger", "sniggering", "retardant", "retardation", "dagobah",
  "chinkara", "spiccato", "haspic", "cockaigne",
];
